import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Facebook, Instagram, Mail } from "lucide-react";
import { Emblem } from "./Emblem";
import { EASE } from "./Reveal";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";

const LINKS = [
  { to: "/concierge", label: "Concierge" },
  { to: "/tickets", label: "Tickets" },
  { to: "/events", label: "Events" },
  { to: "/membership", label: "Membership" },
];

const MOBILE_LINKS = [
  ...LINKS,
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { openEnquiry, loaderDone } = useUI();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const solid = scrolled || open;

  return (
    <>
      <motion.header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-[70] transition-[background-color,border-color,backdrop-filter] duration-[400ms] ${
          solid
            ? "border-b border-[rgba(200,164,93,0.18)] bg-[rgba(5,5,5,0.88)] backdrop-blur-[18px]"
            : "border-b border-transparent bg-transparent"
        }`}
        initial={{ y: -24, opacity: 0 }}
        animate={loaderDone ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
      >
        <div className="mx-auto flex h-20 max-w-[1360px] items-center justify-between px-6 lg:h-[84px] lg:px-12">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3"
            aria-label="MDS Arabama home"
          >
            <Emblem className="h-9 w-auto" />
            <span className="font-serif text-lg font-semibold tracking-[0.16em] text-ivory sm:text-xl">
              MDS ARABAMA
            </span>
          </Link>

          <nav
            className="hidden items-center gap-9 lg:flex"
            aria-label="Primary navigation"
          >
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `nav-link text-[11px] font-light uppercase tracking-[0.24em] transition-colors duration-300 ${
                    isActive ? "text-champagne" : "text-ivory/85 hover:text-ivory"
                  }`
                }
              >
                {({ isActive }) => (
                  <span data-active={isActive} className="nav-link">
                    {l.label}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-4 lg:flex">
            <button
              type="button"
              data-testid="nav-enquire-button"
              onClick={() => openEnquiry("Concierge")}
              className="bg-champagne px-7 py-[11px] text-[10px] font-medium uppercase tracking-[0.26em] text-[#050505] transition-all duration-[350ms] hover:bg-champagne-light hover:shadow-[0_8px_30px_rgba(177,138,68,0.28)]"
            >
              Enquire
            </button>
            <Link
              to={user ? (user.role === "admin" ? "/admin" : "/account") : "/login"}
              data-testid="nav-login-button"
              className="border border-[rgba(200,164,93,0.4)] px-7 py-[10px] text-[10px] font-light uppercase tracking-[0.26em] text-ivory transition-all duration-[350ms] hover:border-champagne hover:bg-champagne/10"
            >
              {user ? (user.role === "admin" ? "Desk" : "Portal") : "Login"}
            </Link>
          </div>

          <button
            type="button"
            data-testid="mobile-menu-button"
            className="flex flex-col gap-[7px] p-2 lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            <motion.span
              animate={open ? { rotate: 45, y: 4 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.3 }}
              className="block h-px w-6 bg-ivory"
            />
            <motion.span
              animate={open ? { rotate: -45, y: -4 } : { rotate: 0, y: 0 }}
              transition={{ duration: 0.3 }}
              className="block h-px w-6 bg-ivory"
            />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[65] flex flex-col justify-center bg-[#050505] px-9"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
              {MOBILE_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 26 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 + i * 0.07, duration: 0.55, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block py-2 font-serif text-[34px] font-medium leading-tight transition-colors duration-300 ${
                        isActive ? "italic text-champagne" : "text-ivory hover:text-champagne"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5, ease: EASE }}
              className="mt-10"
            >
              <button
                type="button"
                data-testid="mobile-enquire-button"
                onClick={() => {
                  setOpen(false);
                  openEnquiry("Concierge");
                }}
                className="bg-champagne px-9 py-4 text-[11px] font-medium uppercase tracking-[0.26em] text-[#050505]"
              >
                Enquire
              </button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              className="absolute bottom-10 left-9 flex items-center gap-6 text-ivory-muted"
            >
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="mobile-social-facebook" className="transition-colors hover:text-champagne">
                <Facebook className="h-4 w-4" strokeWidth={1.25} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="mobile-social-instagram" className="transition-colors hover:text-champagne">
                <Instagram className="h-4 w-4" strokeWidth={1.25} />
              </a>
              <a href="mailto:concierge@mdsarabama.com" aria-label="Email" data-testid="mobile-social-email" className="transition-colors hover:text-champagne">
                <Mail className="h-4 w-4" strokeWidth={1.25} />
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
